/**
 * AKL Summit visualization
 *
 * Creates SVG shapes from given data, and provides ways to
 * interact with it.
 *
 * Most operations are performed on the set of created shapes,
 * or a filtered subset of the shapes. References to all the individual
 * subshapes and labels, as well as a copy of the source data,
 * is stored within this.planets array.
 *
 * Each planet is also given x and y coordinates as percentages
 * relative to the viewport. The planets also have a radius1 and radius2
 * property, which refer to the currently selected data metrics.
 *
 * The general procedure for moving/scaling the planets is carried out
 * by first modifying the planets' data within this.planets array.
 * Once the positional/radius data has been changed, the individual
 * SVG shapes should be updated to reflect the new values.
 * For a practical example of this, check out the implementation of
 * methods setGridPosition() and setLinePosition().
 *
 * Mobile users are given a slightly different experience,
 * in that the overview mode is disabled. This is achieved with a
 * preset breakpoint, which (at the time of writing) matches the
 * mobile breakpoint in stylesheets.
 *
 * To anyone who isn't me; sorry about this mess. It all started as
 * prototype code and evolved into the bowl of spaghetti you see here.
 * No time for proper refactoring: "Next year" – they said.
 */

import Snap, { set } from 'snapsvg';
import Hammer from 'hammerjs';
import flatMap from 'array.prototype.flatmap';
import Debounce from './debounce';
import CruxMeters from './cruxMeters';
import hubspotForm from './hubspotForm';
//import LineChart from './linechart';

// Math shorthand methods
const {
  ceil,
  floor,
  max,
  min,
  sqrt,
  sign
} = Math;

class AKLSummitVis {
  constructor(data = [], details = [], cruxData = []) {
    this.width = 0;
    this.height = 0;
    this.animDuration = 500; // ms
    this.snap = Snap(this.width, this.height);
    this._data = data;
    this._details = details;
    this._cruxData = cruxData;

    this.gridWidth = ceil(sqrt(data.length));

    this.lineYPosition = 50;

    this.mobile = true;
    this.mobileBreakpointX = 800;
    this.mobileBreakpointY = 575;
    this.lineYMobile = 50; // Percentage of view height from top
    this.lineYDesktop = 50; // Percentage of view height from top

    this.targetIndex = 0;

    this.start = 1;
  }

  /* Selects elements and attaches event listeners */
  attach(parent) {
    this.parent = parent;
    parent.appendChild(this.snap.node);

    addEventListener('resize', () => this.fitViewport());

    this.fitViewport(true);

    this.activePositionModel = this.setGridPositions;
    this.searchForm = document.getElementById('search-form');

    this.filtersForm = document.getElementById('aklsummit-controls');
    this.filterFieldset = document.getElementById('filterByType');
    this.sortFieldset = document.getElementById('sortBy');
    this.searchInput = document.getElementById('searchByGroup');
    this.scale1Select = document.getElementById('radius1ByField');
    this.scale2Select = document.getElementById('radius2ByField');
    this.backToGrid = document.getElementById('backButton');
    this.buttonLeft = document.getElementById('moveLeft');
    this.buttonRight = document.getElementById('moveRight');

    this.detailsGroup = document.getElementById('info-group');
    this.detailsTurnover = document.getElementById('info-turnover');
    this.detailsPositionTurnover = document.getElementById('info-position-turnover');
    this.detailsTurnoverChange = document.getElementById('info-turnover_change');
    this.detailsCrasmanIndex = document.getElementById('info-crasman_index');
    this.detailsPositionCrasmanIndex = document.getElementById('info-position-crasman_index');
    this.detailsCrasmanIndexChange = document.getElementById('info-crasman_index_change');

    this.detailsSite = document.getElementById('info-site-crasman-index');
    this.detailsSiteCrasmanIndex = document.getElementById('info-site-crasman_index');
    this.detailsSitePositionCrasmanIndex = document.getElementById('info-site-position-crasman_index');
    this.detailsSiteCrasmanIndexChange = document.getElementById('info-site-crasman_index_change')
    this.detailsSiteTarget = document.getElementById('info-site-target');

    this.detailsTarget = document.getElementById('info-target');
    this.detailsTargets = document.getElementById('info-site-details');

    this.detailsTraffic = document.getElementById('info-site-total_traffic');
    this.detailsTrafficChange = document.getElementById('info-site-total_traffic_change');
    this.detailsPositionTraffic = document.getElementById('info-position-total_traffic');
    this.detailsTotalKeywords = document.getElementById('info-site-total_keywords');
    this.detailsTotalKeywordsChange = document.getElementById('info-site-total_keywords_change');
    this.detailsPositionTotalKeywords = document.getElementById('info-position-total_keywords');
    this.detailsBacklinks = document.getElementById('info-site-total_backlinks');
    this.detailsBacklinksChange = document.getElementById('info-site-total_backlinks_change');
    this.detailsPositionBacklinks = document.getElementById('info-position-total_backlinks');

    this.detailsKeywordsYear = document.getElementById('most-valueable-keywords-year');
    this.detailsKeywords = document.getElementById('most-valuable-keywords');

    //crux data
    this.cruxDataContainer = document.getElementById('site-crux-data');

    this.createPlanets();
    this.setSortSource(this.sortFieldset.querySelector('input:checked').value);
    this.filterBy('type', this.getFilterArray(this.filterFieldset));


    // Small delay before transitioning planets into formation
    setTimeout(() => this.activePositionModel(), 500);

     //show hubspot form after some time
    hubspotForm();


    this.snap.hover(
      event => this.handleHoverIn(event),
      () => {
        if(this.activePositionModel !== this.setLinePositions) this.hideLabels();
      }
    );

    [this.searchForm, this.filtersForm].forEach(formEl => formEl.addEventListener('submit', ev => {
      ev.preventDefault();


      if(this.currentPlanet && this.activePositionModel === this.setGridPositions) {
        this.activePositionModel = this.setLinePositions;
      } else {
        this.activePositionModel = this.setGridPositions;
      }
      this.activePositionModel();
    }));

    this.searchInput.addEventListener('input', (ev) => {
      document.body.classList.add('overview');

      if(ev.currentTarget.value === '') {
        this.clearHighlights();
        this.hideLabels();
        this.searchHighlight(false);
      } else {
        this.searchHighlight(ev.currentTarget.value);
      }
    });

    this.searchInput.addEventListener('blur', ev => {
      this.scrollToView();
    })


    this.filterFieldset.addEventListener('change', (ev) => {
      const activeFilters = this.getFilterArray(ev.currentTarget);
      // If only one checkbox is checked, disable it to prevent blank selection
      if(activeFilters.length <= 1) {
        const remainingCheckbox = ev.currentTarget.querySelector('input:checked');
        remainingCheckbox.disabled = true;
      } else {
        const disabledCheckbox = ev.currentTarget.querySelector('input:disabled');
        if(disabledCheckbox) disabledCheckbox.disabled = false;
      }
      this.filterBy('type', activeFilters);
      this.hideHidden();

      // Find the new index of the currently selected planet, if possible
      const newIndex = this.getSortedPlanets().indexOf(this.currentPlanet)
      this.targetIndex = newIndex < 0 ? 0 : newIndex;
      this.activePositionModel();
    });

    this.sortFieldset.addEventListener('change', ev => {
      const chosenRadio = ev.currentTarget.querySelector('input:checked');
      switch(chosenRadio.value) {
        case 'radius1':
          this.sortSource = 'scale1';
          this.sortField = this.scale1Select.value;
          break;
        case 'radius2':
          this.sortSource = 'scale2';
          this.sortField = this.scale2Select.value;
          break;
        case 'alphanumeric':
        default:
          this.sortSource = 'default';
          this.sortField = 'group';
      }
      this.activePositionModel();
    });

    this.scale1Select.addEventListener('change', (ev) => {
      if(this.sortSource === 'scale1') {
        this.sortField = ev.currentTarget.value;
      }
      this.activePositionModel();
    });

    this.scale2Select.addEventListener('change', (ev) => {
      if(this.sortSource === 'scale2') {
        this.sortField = ev.currentTarget.value;
      }
      this.activePositionModel();
    });

    this.backToGrid.addEventListener('click', ev => {
      document.body.classList.add('overview');
      this.currentPlanet = false;
      this.activePositionModel = this.setGridPositions;
      this.activePositionModel();
      this.fitViewport();
    });

    addEventListener('keydown', evt => {
      if(evt.target.tagName.toLowerCase() === 'input') return;
      if(
        (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight')
      ) {
        this.moveLinePos(evt.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    const hammer = new Hammer(this.parent);
    hammer.on('swipe', evt => {
      this.moveLinePos(0 - sign(evt.deltaX));
    });

    this.buttonLeft.addEventListener('click', evt => {
      this.moveLinePos(-1);
    });
    this.buttonRight.addEventListener('click', evt => {
      this.moveLinePos(1);
    });

    this.parent.addEventListener('click', evt => {
      // check if the user clicked a planet
      const clickedPlanet = this.planets.find(planet => planet.area.node === evt.target);
      if(clickedPlanet) {
        // Zoom in, if we're in grid mode
        document.body.classList.remove('overview');
        window.scrollTo(0, 0);
        this.recalculatHeight();

        if(
          this.activePositionModel === this.setGridPositions ||
          this.currentPlanet !== clickedPlanet
        ) {
          document.body.classList.remove('overview');
          const clickedPlanetIndex = this.getSortedPlanets().indexOf(clickedPlanet);
          this.currentPlanet = clickedPlanet;
          this.targetIndex = clickedPlanetIndex;
          this.activePositionModel = this.setLinePositions;
        }

        this.activePositionModel();
      }
    });

    this.detailsTargets.addEventListener('change', evt => {
      const uuid = evt.target.value;
      const site = this._details.find(d => d.uuid === uuid);
      const cruxData = this._cruxData.find(d => d.origin.includes(site.target))
      if(site) {
        this.displaySiteDetails(site, this.getVisiblePlanets(), cruxData);
      }
    });

    this.detailsKeywordsYear.addEventListener('change', evt => {
      const detailsUuid = this.detailsTargets.value;
      const selectedDetails = this._details.find(d => d.uuid === detailsUuid);
      console.log({
        planetData: this.currentPlanet.data,
        siteData: selectedDetails
      });
      if(selectedDetails && selectedDetails.group === this.currentPlanet.data.group) {
        this.updateSiteKeywords(selectedDetails);
      } else {
        this.updateSiteKeywords(this.currentPlanet.data);
      }
    });
  }


  setSortSource(field) {
    switch(field) {
      case 'radius1':
        this.sortSource = 'scale1';
        this.sortField = this.scale1Select.value;
        break;
      case 'radius2':
        this.sortSource = 'scale2';
        this.sortField = this.scale2Select.value;
        break;
      case 'alphanumeric':
      default:
        this.sortSource = 'default';
        this.sortField = 'group';
    }
  }

  // Adjust the planet's X-offset in line view mode
  // Takes an offset argument, relative to the currently centered planet
  moveLinePos(offset) {
    if(this.activePositionModel !== this.setLinePositions) return;
    const visiblePlanets = this.getVisiblePlanets();
    this.targetIndex += offset;
    if(this.targetIndex < 0) {
      this.targetIndex = 0;
    } else if(this.targetIndex >= visiblePlanets.length) {
      this.targetIndex = visiblePlanets.length - 1;
    }
    this.activePositionModel(this.targetIndex);
  }

  // Returns a sorted list of non-hidden planets
  getSortedPlanets() {
    return this.getVisiblePlanets().sort(this.sortPlanets(this.sortField));
  }

  // Returns a list of non-hidden planets
  getVisiblePlanets() {
    return this.planets.filter(p => !p.hidden);
  }

  // Returns an array sort function, comparing with the given field name
  sortPlanets(field) {
    return (a, b) => {
      const pA = a.data[field];
      const pB = b.data[field];
      if(typeof pA === 'number' && typeof pB === 'number') {
        return pB - pA;
      }
      if(pA > pB) return 1;
      if(pA < pB) return -1;
      return 0;
    };
  }

  /* Scrolls the view so that the canvas is visible */
  scrollToView() {
    const elem = document.querySelector('.highlight');
    if(!elem) {
      document.documentElement.scrollTo({
        top: this.parent.offsetTop,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      elem.scrollIntoView({
        top: this.parent.offsetTop - 100,
        left: 0,
        behavior: 'smooth'
      });
    }


  }

  /* Scales the presentation to screen/container size */
  fitViewport(initial = false) {

    document.body.classList.add('overview');
    const [targetWidth, targetHeight] = [
      this.parent.offsetWidth ? this.parent.offsetWidth : innerWidth,
      this.parent.offsetHeight ? this.parent.offsetHeight : innerHeight
    ];
    if(this.height !== targetHeight || this.width !== targetWidth) {

      this.height = targetHeight;
      this.width = targetWidth;
      this.snap.paper.attr({
        height:  '100%',
        width: this.width
      });

      if(!initial) {
        if (this.currentPlanet) {
          this.activePositionModel = this.setLinePositions;
          document.body.classList.remove('overview');
        }
        this.scaleAreas();
        this.showLabel();
        this.recalculatHeight();

      }
    }

    this.mobile = innerWidth < this.mobileBreakpointX || innerHeight < this.mobileBreakpointY;
  }

  recalculatHeight() {
    this.height = this.parent.offsetHeight;
    this.snap.paper.attr({
      height: '100%'
    });
    this.activePositionModel();
  }
  /* Return an array of the values of all checked checkboxes within the given fieldset */
  getFilterArray(fieldsetEl) {
    const checkboxes = Array.from(
      fieldsetEl.querySelectorAll('input[type="checkbox"]')
    );

    const values = checkboxes.filter(cb => cb.checked).map(cb => cb.value);

    return values;
  }

  // Creates the SVG structure for each data point
  createPlanets() {
    const squareSize = this.gridSquareSize();
    this.planets = this._data.map((d, i, data) => {
      const area = this.snap.circle('50%', '50%', squareSize / 2);
      area.addClass('planet-area');
      const circle1 = this.snap.circle('50%', '50%', 1);
      circle1.addClass('planet-circle1');
      const circle2 = this.snap.circle('50%', '50%', 1);
      circle2.addClass('planet-circle2');

      const group = this.snap.group(area, circle1, circle2);
      group.addClass('planet-group')
      group.attr('id', d.uuid);

      const label = document.querySelector(`[data-planet="${ d.uuid }"]`);

      return {
        data: d,
        group, area, circle1, circle2, label,
        x: 50, y: 50
      };
    });
  }

  // Determines the size available per planet
  gridSquareSize() {
    return Math.min(this.width, this.height) / this.gridWidth;
  }

  // Pushes hidden planets out of the view
  hideHidden() {
    const y = this.mobile ? this.lineYMobile : this.lineYDesktop;
    this.getVisiblePlanets().forEach(p => {
      p.x = -25;
      p.y = y;
    });
  }

  // Displays the label for the currently hovered planet
  handleHoverIn(event) {
    if(this.activePositionModel === this.setLinePositions) return;
    const planet = this.planets.find(planet => planet.area.node === event.target);
    if(planet && !planet.label.classList.contains('visible')) {
      this.showLabel(planet);
    }
  }

  // Hides all visible labels
  hideLabels() {
    document.querySelectorAll('[data-planet].visible')
      .forEach(el => el.classList.remove('visible'));
  }

  // Shows the given planet's associated label
  showLabel(planet = this.currentPlanet) {

    if(
      !planet || planet.hidden || // Don't show label if the planet is filtered out
      planet.x < 0 || planet.x > 100 // ...or past the view edges
    ) {
      try {
        planet.label.classList.remove('visible');
      } catch(e) { /* probably missing the planet */ }
      return;
    }

    const cx = min(110, max(planet.x, -10));

    let { y } = planet.circle2.node.getBBox();

    const maxX = this.parent.offsetWidth;
    const maxY = this.parent.offsetHeight;

    if(
      cx < 0 || cx > maxX ||
      y < 0 || y > maxY
    ) {
      // Don't show label if it's not within display area
      planet.label.classList.remove('visible');
      return;
    }

    // Move the label to planet's position
    planet.label.style.left = cx + '%';
    planet.label.style.top = y + 'px';
    planet.label.classList.add('visible');
  }

  // Resets hidden planets to visible state
  clearFilters() {
    this.planets.forEach(planet => planet.hidden = false);
  }

  planetMatcher(needle) {

    if (needle === '' || needle === false) return;

    const searchString = needle.toLowerCase();
    const checkKeys = ['group', 'target'];
    const matchingPlanets = this.getVisiblePlanets().filter(planet => {
      const planetMatches = checkKeys.reduce((matches, key) => {
        return matches || planet.data[key].toLowerCase().includes(searchString)
      }, false);
      const sites = planet.data.target === ''
        ? this._details.filter(detail => detail.group === planet.data.group)
        : [];
      const siteMatches = sites.length > 0
        ? sites.reduce((found, site) => {
          return found || checkKeys.reduce((matches, key) => {
            return matches || site[key].toLowerCase().includes(searchString);
          }, false);
        }, false)
        : false;

      return planetMatches || siteMatches;
    });

    return matchingPlanets;
  }

  // Highlights planets that's given data value (partially) matches the search string
  // TODO: Refactor this bowl of bolognese...
  searchHighlight(search) {

    this.currentPlanet = false;
    this.hideLabels();
    this.fitViewport();

    this.planets.forEach(planet => {
      planet.group.node.classList.remove('highlight');
    });


    const matchingPlanets = this.planetMatcher(search);

    if (matchingPlanets === undefined) return;

    // Highlight matches visually on desktop
    matchingPlanets.forEach(planet => planet.group.node.classList.add('highlight'));
    matchingPlanets.forEach(planet => this.hideLabels());

    // Check for exact match
    if(matchingPlanets.length === 1) {

      // TODO: pre-select the searched site in the site dropdown
      this.currentPlanet = matchingPlanets[0];
      this.targetIndex = this.getSortedPlanets().indexOf(this.currentPlanet);
      this.showLabel();
      if(this.activePositionModel === this.setLinePositions) {
        this.activePositionModel();
      }
      //this.scrollToView();
      return; // match found, break early
    }

    // Check for multiple matches
    if(
      matchingPlanets.length > 1
    ) {


      //this.scrollToView();
      this.activePositionModel = this.setGridPositions;
      this.activePositionModel();

      setTimeout(() => {
        if (!this.mobile) { matchingPlanets.forEach(planet => this.showLabel(planet)); }
      }, this.animDuration - 100);



    } else { // No matches
      this.currentPlanet = false;
      this.hideLabels();
    }
  }

  // Resets all highlighted planets
  clearHighlights() {
    document.querySelectorAll('.highlight').forEach(el => el.classList.remove('highlight'));
  }

  // Hides planets that don't match the given value
  filterBy(key, values = []) {
    this.planets.forEach(planet => {
      const visible = values.reduce((result, value) => {
        return result || planet.data[key].includes(value);
      }, false);
      planet.hidden = !visible;
    });
  }

  // Calculates and sets each planet to their corresponding grid coordinate
  setGridPositions() {

    this.gridWidth = ceil(sqrt(this.getVisiblePlanets().length));
    this.getSortedPlanets().forEach((planet, index) => {
      const { x, y } = this.findGridPosition(index);
      planet.x = x;
      planet.y = y;
    });
    this.moveAreasToPositions();
    this.moveCirclesToPositions();
    this.setScalesByValue(
      this.scale1Select.options[this.scale1Select.selectedIndex].value,
      this.scale2Select.options[this.scale2Select.selectedIndex].value
    );
    if(this.currentPlanet) {
      this.showLabel();
    }

  }

  // Calculates and sets each planet to their corresponding line coordinate
  setLinePositions(targetIndex = this.targetIndex) {

    this.clearHighlights();
    this.gridWidth = 3;
    const y = this.mobile ? this.lineYMobile : this.lineYDesktop;
    this.sortedPlanets = this.getSortedPlanets();
    this.sortedPlanets.forEach((planet, index) => {
      planet.y = y;
      planet.x = 50 + (index - targetIndex) * (100 / 3);
    });
    this.moveAreasToPositions();
    this.moveCirclesToPositions();
    this.setScalesByValue(
      this.scale1Select.options[this.scale1Select.selectedIndex].value,
      this.scale2Select.options[this.scale2Select.selectedIndex].value
    );
    this.hideLabels();
    this.currentPlanet = this.sortedPlanets[targetIndex];
    this.displayDetails();


    //timeout for labels to be visible at correct position
    setTimeout(() => this.showLabel(), this.animDuration - 100);

  }

  // Source data may contain wacky characters, this attempts to sanitize it
  parseChangeValue(change) {
    if(typeof change === 'number') return change;
    if(change === '') return null;
    let result;
    result = Math.round(change.replace(',', '.').replace('−', '-'));
    if(Number.isNaN(result)) return null;
    return result;
  }

  getChangeClass(change) {
    const changeNumber = this.parseChangeValue(change);
    if(typeof changeNumber !== 'number') return '';
    if(changeNumber === 0) return '';
    return changeNumber < 0 ? 'fall' : 'rise';
  }

  formatChangeValue(change) {
    if(change === '') return '-';
    return this.parseChangeValue(change) + '%';
  }

  // Populate and display a planet's detailed data views
  displayDetails() {
    const {
      group, target,
      crasman_index, crasman_index_change,
      turnover, turnover_change
    } = this.currentPlanet.data;

    this.detailsGroup.innerText = group;
    this.detailsTurnover.innerText = turnover;
    this.detailsTurnoverChange.innerText = this.formatChangeValue(turnover_change);
    this.detailsTurnoverChange.className = this.getChangeClass(turnover_change);
    this.detailsCrasmanIndex.innerText = crasman_index;
    this.detailsCrasmanIndexChange.innerText = this.formatChangeValue(crasman_index_change);
    this.detailsCrasmanIndexChange.className = this.getChangeClass(crasman_index_change);

    let site = this.currentPlanet.data;


    let cruxData = [];


    if(!target || target.length === 0) { // Group planet
      this.detailsTarget.innerText = '';
      const sites = this._details.filter(d => d.group === group);
      const currentSearchTerm = this.searchInput.value.toLowerCase();
      this.currentSite = sites.find(site => site.target.toLowerCase().includes(currentSearchTerm));

      //find current crux data for group
      cruxData = this._cruxData.find(d => d.origin.includes(this.currentSite.target))

      this.displaySitesOptions(sites, currentSearchTerm);
      this.detailsTargets.parentNode.classList.remove('hidden');
      this.detailsSite.classList.remove('hidden');
      this.displaySiteDetails(this.currentSite ? this.currentSite : sites[0], this.sortedPlanets, cruxData);

    } else { // Single site planet
      this.detailsSite.classList.add('hidden');
      this.detailsTargets.parentNode.classList.add('hidden');
      this.detailsTarget.innerText = target.replace(/^www\./,'');

      //find current crux data for single planet
      cruxData = this._cruxData.find(d => d.origin.includes(target))


      this.displaySiteDetails(this.currentPlanet.data, this.sortedPlanets, cruxData);
    }

    const turnoverPosition = this.getPlanetPosition(site.uuid, 'turnover', this.sortedPlanets);
    this.detailsPositionTurnover.innerText = turnoverPosition.current + '/' + turnoverPosition.total;

    const crasmanIndexPosition = this.getPlanetPosition(site.uuid, 'crasman_index', this.sortedPlanets);
    this.detailsPositionCrasmanIndex.innerText = crasmanIndexPosition.current + '/' + crasmanIndexPosition.total;
  }

  displaySitesOptions(sites, searchTerm) {
    this.detailsTargets.innerHTML = sites.map((site, index) => {
      return `<option value="${
        site.uuid
      }"${
        searchTerm !== '' && site.target.toLowerCase().includes(searchTerm) ? ' selected' : ''
      }>${
        site.target.replace(/^www\./,'')
      }</option>`;
    }).join('');
  }

  getPlanetPosition(uuid, key, planets) {
    const sortedPlanets = planets.sort((a, b) => b.data[key] - a.data[key]);
    return {
      current: sortedPlanets.findIndex(p => p.data.uuid === uuid) + 1,
      total: planets.length
    };
  }

  getSitePosition(uuid, key, planets) {

    const currentSites = flatMap(planets, planet => {
      if(planet.data.target !== '') return planet.data;
      return this._details.filter(detail => detail.group === planet.data.group);
    }).sort((a, b) => b[key] - a[key]);
    return {
      current: currentSites.findIndex(site => site.uuid === uuid) + 1,
      total: currentSites.length
    };
  }

  getSiteMetricPosition(uuid, key, target) {

    /* sort cruxdata by metrics.key.histrogram[0] value based asc */
    const sortedCruxData = this._cruxData.sort((a, b) => {

      if (a.metrics !== undefined && a.metrics[key] && b.metrics !== undefined && b.metrics[key]) {
        const aMetric = a.metrics[key].histogram[0].density;
        const bMetric = b.metrics[key].histogram[0].density;
        return bMetric - aMetric;
      }

    });

    return {
      current: sortedCruxData.findIndex(cruxData => cruxData.origin.includes(target)) + 1,
      total: sortedCruxData.length
    };

  }



  displaySiteDetails(site, planets, cruxData) {
    const {
      total_traffic, total_traffic_change,
      total_keywords, total_keywords_change,
      total_backlinks, total_backlinks_change
    } = site;



    if (cruxData !== undefined) {
      this.cruxDataContainer.parentElement.classList.remove('hidden');

      const crux = CruxMeters();
      crux.init(cruxData.metrics, this.cruxDataContainer);


    } else {
      this.cruxDataContainer.parentElement.classList.add('hidden');
      this.cruxDataContainer.innerHTML = '';
    }

    this.detailsTraffic.innerText = total_traffic;
    this.detailsTrafficChange.innerText = this.formatChangeValue(total_traffic_change);
    this.detailsTrafficChange.className = this.getChangeClass(total_traffic_change);
    const positionTotalTraffic = this.getSitePosition(site.uuid, 'total_traffic', planets);
    this.detailsPositionTraffic.innerText = positionTotalTraffic.current + '/' + positionTotalTraffic.total;

    this.detailsTotalKeywords.innerText = total_keywords;
    this.detailsTotalKeywordsChange.innerText = this.formatChangeValue(total_keywords_change);
    this.detailsTotalKeywordsChange.className = this.getChangeClass(total_keywords_change);
    const positionTotalKeywords = this.getSitePosition(site.uuid, 'total_keywords', planets);
    this.detailsPositionTotalKeywords.innerText = positionTotalKeywords.current + '/' + positionTotalKeywords.total;

    this.detailsBacklinks.innerText = total_backlinks;
    this.detailsBacklinksChange.innerText = this.formatChangeValue(total_backlinks_change);
    this.detailsBacklinksChange.className = this.getChangeClass(total_backlinks_change);
    const positionTotalBacklinks = this.getSitePosition(site.uuid, 'total_backlinks', planets);
    this.detailsPositionBacklinks.innerText = positionTotalBacklinks.current + '/' + positionTotalBacklinks.total;

    this.detailsSiteCrasmanIndex.innerText = site.crasman_index;
    this.detailsSiteCrasmanIndexChange.innerText = this.formatChangeValue(site.crasman_index_change);
    this.detailsSiteCrasmanIndexChange.className = this.getChangeClass(site.crasman_index_change);
    const positionSiteCrasmanIndex = this.getSitePosition(site.uuid, 'crasman_index', planets);
    this.detailsSitePositionCrasmanIndex.innerText = positionSiteCrasmanIndex.current + '/' + positionSiteCrasmanIndex.total;

    this.detailsSiteTarget.innerText = site.target;

    this.updateSiteKeywords(site);
  }

  updateSiteKeywords(site) {
    const keywords = this.detailsKeywordsYear.value === 'current'
      ? site.most_valuable_keywords || []
      : site.most_valuable_keywords_prev || [];
    this.detailsKeywords.innerHTML = keywords.map((keyword, index) => index < 5 ? `<li>${
      keyword[0].toUpperCase() + keyword.slice(1)
    }</li>` : '').join('');
  }

  // Determines the grid position by a planet's index and the desired number of columns
  findGridPosition(index, gridWidth = this.gridWidth) {
    const x = (100 / gridWidth / 2) + ((index % gridWidth) / gridWidth * 100);
    const y = (100 / gridWidth / 2) + (floor(index / gridWidth) / gridWidth * 100);
    return { x, y };
  }

  // Moves each planet's hover area to it's current position
  moveAreasToPositions() {
    this.planets.forEach(planet => {
      if(
        planet.x < 0 || planet.x > 100 ||
        planet.y < 0 || planet.y > 100
      ) {
        planet.area.attr({ display: 'none' });
        return;
      }
      planet.area.attr({
        cx: planet.x + '%',
        cy: planet.y + '%',
        display: null
      })
    });
    this.scaleAreas();
  }

  // Transitions each planet's circles to it's current position
  moveCirclesToPositions() {
    const y = this.mobile ? this.lineYMobile : this.lineYDesktop;
    this.planets.forEach(planet => {
      // Fade out planets that are positioned beyond the view area
      if(
        planet.x < 0 || planet.x > 100 ||
        planet.y < 0 || planet.y > 100
        ) {
        // clamp x value near view edges
        const tx = min(110, max(planet.x, -10));
        planet.circle1.stop().animate({
          cx: tx + '%', cy: y + '%',
          opacity: 0
        }, this.animDuration / 2, this.easeOut);
        planet.circle2.stop().animate({
          cx: tx + '%', cy: y + '%',
          opacity: 0
        }, this.animDuration / 2, this.easeOut);
        return;
      }

      planet.circle1.stop().animate({
        cx: planet.x + '%',
        cy: planet.y + '%',
        opacity: 1
      }, this.animDuration, this.easeOut);

      planet.circle2.stop().animate({
        cx: planet.x + '%',
        cy: planet.y + '%',
        opacity: 1
      }, this.animDuration, this.easeOut);
    });
  }

  // Determines and sets an appropriate scale for each planet's circles
  setScalesByValue(key1, key2) {
    const valuesRange1 = this.valuesRange(key1);
    const valuesRange2 = this.valuesRange(key2);
    this.planets.forEach(planet => {
      planet.radius1 = planet.hidden ? 0 : 1 + max(
        0,
        (planet.data[key1] / valuesRange1.max) * (
          1 / this.gridWidth * Math.min(this.height, this.width) / 2
        )
      );
      planet.radius2 = planet.hidden ? 0 : 1 + max(
        0,
        (planet.data[key2] / valuesRange2.max) * (
          1 / this.gridWidth * Math.min(this.height, this.width) / 2
        )
      );
    });

    this.scalePlanets();
  }

  // Sets an appropriate size for all planets' hit area
  scaleAreas() {
    const squareSize = this.gridSquareSize();
    this.planets.forEach(planet => planet.area.attr({
      r: planet.hidden ? 0 : squareSize / 2
    }));
  }

  // Transitions each planet's circles to the current radii
  scalePlanets() {
    this.planets.forEach(planet => {
      planet.circle1.animate({
        r: planet.radius1
      }, this.animDuration, this.easeOut);
      planet.circle2.animate({
        r: planet.radius2
      }, this.animDuration, this.easeOut);
    });
  }

  // Finds the min and max values for a given key, excluding hidden planets
  valuesRange(key) {
    const values = this.getVisiblePlanets().map(p => p.data[key] || 0);
    return {
      min: min(...values),
      max: max(1, ...values)
    };
  }

  // Math. Not even once.
  easeOut (t) {
    return (--t) * t * t + 1;
  }
}

export default AKLSummitVis;
